// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useSessionStore from '@/app/store/session';
import actionsRemoteCollections from '../../api/actionsRemoteCollections';
// Components
import ModalSendRemoteCollectionByMail from '../modals/ModalSendRemoteCollectionByMail';
import Dropdown from '@/common-utils/components/renders/dropdown/Dropdown';
import Entity from '@/common-utils/components/renders/entity/Entity';
// Common
import ICONS from '@/common-utils/enums/icons';
import remoteCollectionFields from '../../fields/remoteCollectionFields';
import Permissions from '@/app/permissions/Permissions';
import { findApplicationFromCode } from '@/app/modules/contracts/enums/ItpRegex';
import { useToast } from '@/common-utils/hooks/use-toast';
// lang
import { nsRemoteCollection } from '@/app/i18n/namespaces';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
/**
 *
 * @param root0
 * @param root0.remoteCollection
 * @param root0.setLaunchRefetch
 * @returns {JSX} DropdownRemoteCollectionsActionsRow
 */
function DropdownRemoteCollectionsActionsRow({ remoteCollection, setLaunchRefetch }) {

    // Store
    const permissions = useSessionStore(s => s.session.permissions);

    // State
    const [showModalSendMailRemoteCollection, setShowModalSendMailRemoteCollection] = useState(false);

    // Toast
    const { toast } = useToast();

    // API Calls
    const submitRefreshAlmaTransactions = actionsRemoteCollections.refreshAlmaTransactions(() => { }, () => { });

    // Functions
    const shouldShowSendMail = () => {
        const applications = remoteCollection ? remoteCollection[remoteCollectionFields.APPLICATION.key] : false;
        const actionsMailToDisplay = applications ? findApplicationFromCode(applications) : false;
        return actionsMailToDisplay && actionsMailToDisplay.mail;
    };
    const shouldShowManualUpdate = () => {
        const applications = remoteCollection ? remoteCollection[remoteCollectionFields.APPLICATION.key] : false;
        const actionsUpdateToDisplay = applications ? findApplicationFromCode(applications) : false;
        return actionsUpdateToDisplay && actionsUpdateToDisplay.manualUpdate;
    };
    const refreshTransactionAlma = async () => {
        toast({
            description: <Entity entity='toast.refresh.loading' namespace={nsRemoteCollection} />,
            variant: 'default'
        });

        const reconciliationNumber = remoteCollection[remoteCollectionFields.RECONCILIATION_NUMBER.key];
        const contractNumber = remoteCollection[remoteCollectionFields.CONTRACT.key];
        const application = remoteCollection[remoteCollectionFields.APPLICATION.key];
        try {
            // launch refresh call
            await submitRefreshAlmaTransactions.mutateAsync({
                reconciliationNumber: reconciliationNumber,
                contractNumber: contractNumber,
                application: application
            });
            // handle error from api
            if (submitRefreshAlmaTransactions.isError) {
                return (
                    toast({
                        description: <Entity entity='toast.refresh.error' namespace={nsRemoteCollection} />,
                        variant: 'danger'
                    })
                );
            } else {
                return (
                    setLaunchRefetch(true),
                    toast({
                        description: <Entity entity='toast.refresh.success' namespace={nsRemoteCollection} />,
                        variant: 'success'
                    })
                );
            }
        }
        // eslint-disable-next-line no-unused-vars
        catch (error) {
            setLaunchRefetch(true);
            return (
                toast({
                    description: <Entity entity='toast.refresh.error' namespace={nsRemoteCollection} />,
                    variant: 'danger'
                })
            );
        }
    };

    return (
        <>
            <Dropdown
                triggerValues={{ variant: 'ghostNoBorder', icon: ICONS.MORE }}
                actions={[
                    {
                        icon: ICONS.CONTACT_MAIL,
                        entity: 'actions.row.send_by_mail',
                        namespace: nsRemoteCollection,
                        onClick: () => setShowModalSendMailRemoteCollection(true),
                        visible: shouldShowSendMail(),
                        disabled: !hasRequiredPermissions({
                            permissions: permissions,
                            requiredPermissions: [Permissions.PAYMENT_SEND_REMOTE_COLLECTION_NOTIFICATION.name]
                        }),
                        tooltipForbidden: 'title.permissions.access_denied'
                    },
                    {
                        icon: ICONS.REFRESH,
                        entity: 'actions.row.refresh_transaction',
                        namespace: nsRemoteCollection,
                        className: 'text-warning',
                        onClick: () => refreshTransactionAlma(),
                        visible: shouldShowManualUpdate()
                    }
                ]}
            />
            {
                showModalSendMailRemoteCollection &&
                <ModalSendRemoteCollectionByMail
                    isOpen={showModalSendMailRemoteCollection}
                    setIsOpen={setShowModalSendMailRemoteCollection}
                    remoteCollection={remoteCollection}
                />
            }
        </>
    );
}

export default DropdownRemoteCollectionsActionsRow;

DropdownRemoteCollectionsActionsRow.propTypes = {
    remoteCollection: PropTypes.shape({
        uuid: PropTypes.string,
        [remoteCollectionFields.APPLICATION.key]: PropTypes.string,
        [remoteCollectionFields.RECONCILIATION_NUMBER.key]: PropTypes.string,
        [remoteCollectionFields.CONTRACT.key]: PropTypes.string
    }).isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};
